/* Data Wrangler — Tabulator grid tab for DB Inspector tables */

import { state, refs, esc } from './state.js';
import { api } from './api.js';
import { showPanel } from './panel-registry.js';
import { renderWorkspaceTabs, switchWorkspaceTab } from './search.js';
import { addRecentTable } from './db-inspector.js';

const PAGE_SIZES = [10, 20, 50, 100, 200, 500];
const DEFAULT_PAGE_SIZE = 50;

// ---------------------------------------------------------------------------
// Open a table as a tab
// ---------------------------------------------------------------------------

export function openTableTab(tableName) {
  // Check if already open
  let tab = state.workspaceTabs.find(t => t.type === 'table' && t.tableName === tableName);
  if (tab) {
    switchWorkspaceTab(tab.id);
    return;
  }

  state.wsTabIdCounter++;
  tab = {
    id: 'tab-' + state.wsTabIdCounter,
    label: tableName,
    type: 'table',
    tableName,
    // Table tab state
    _schema: null,
    _stats: null,
    _tabulator: null,
    _pageSize: DEFAULT_PAGE_SIZE,
    _orderBy: '',
    _dir: 'asc',
    _filters: '',
    _totalCount: 0,
    _currentPage: 1,
  };
  state.workspaceTabs.push(tab);
  renderWorkspaceTabs();
  switchWorkspaceTab(tab.id);
}

// ---------------------------------------------------------------------------
// Render the table viewer in the editor panel
// ---------------------------------------------------------------------------

export function renderTableViewer(tab) {
  const viewer = refs.fileViewer;
  if (!viewer) return;

  // Show editor panel
  showPanel('editor');

  viewer.innerHTML = `
    <div class="data-wrangler">
      <div class="dw-header">
        <span class="dw-table-name">${esc(tab.tableName)}</span>
        <span class="dw-row-count" id="dwRowCount"></span>
        <div class="dw-toolbar">
          <label class="dw-page-label">Page size:
            <select class="dw-page-select" id="dwPageSize">
              ${PAGE_SIZES.map(s => `<option value="${s}" ${s === tab._pageSize ? 'selected' : ''}>${s}</option>`).join('')}
            </select>
          </label>
          <button class="btn btn-xs" id="dwRefresh" title="Refresh data">Refresh</button>
        </div>
      </div>
      <div class="dw-grid" id="dwGrid"></div>
    </div>
  `;

  // Wire events
  const pageSel = document.getElementById('dwPageSize');
  if (pageSel) {
    pageSel.addEventListener('change', () => {
      tab._pageSize = parseInt(pageSel.value, 10);
      tab._currentPage = 1;
      _fetchData(tab);
    });
  }

  const refreshBtn = document.getElementById('dwRefresh');
  if (refreshBtn) {
    refreshBtn.addEventListener('click', () => {
      tab._schema = null;
      tab._stats = null;
      if (tab._tabulator) { tab._tabulator.destroy(); tab._tabulator = null; }
      renderTableViewer(tab);
    });
  }

  // Load data
  _initTable(tab);
}

// ---------------------------------------------------------------------------
// Data loading pipeline
// ---------------------------------------------------------------------------

async function _initTable(tab) {
  const gridEl = document.getElementById('dwGrid');
  if (gridEl) gridEl.innerHTML = '<div class="dw-loading">Loading schema...</div>';

  try {
    // 1. Fetch schema (needed for column whitelist)
    if (!tab._schema) {
      const schemaData = await api.dbSchema(tab.tableName);
      tab._schema = schemaData.columns;
    }

    // 2. Build Tabulator with columns from schema
    _buildTabulator(tab);

    // 3. Fetch first page + stats in parallel
    await Promise.all([
      _fetchData(tab),
      _fetchStats(tab),
    ]);
  } catch (err) {
    if (gridEl) gridEl.innerHTML = `<div class="dw-loading dw-error">Error: ${esc(err.message)}</div>`;
  }
}

function _buildTabulator(tab) {
  const gridEl = document.getElementById('dwGrid');
  if (!gridEl || !tab._schema) return;

  gridEl.innerHTML = '';

  const columns = tab._schema.map(col => ({
    title: col.name,
    field: col.name,
    headerSort: true,
    headerFilter: 'input',
    resizable: true,
    tooltip: `${col.type}${col.maxLength ? `(${col.maxLength})` : ''} ${col.nullable ? 'NULL' : 'NOT NULL'}`,
    titleFormatter: function(cell) {
      const colName = cell.getValue();
      const el = document.createElement('div');
      el.className = 'dw-col-header';

      const nameEl = document.createElement('div');
      nameEl.className = 'dw-col-name';
      nameEl.textContent = colName;
      el.appendChild(nameEl);

      // Type badge
      const schemaCol = tab._schema.find(c => c.name === colName);
      if (schemaCol) {
        const typeEl = document.createElement('span');
        typeEl.className = 'dw-col-type';
        typeEl.textContent = schemaCol.type;
        el.appendChild(typeEl);
      }

      // Stats card (populated async)
      const statsEl = document.createElement('div');
      statsEl.className = 'dw-col-stats';
      statsEl.id = `dw-stats-${colName}`;
      el.appendChild(statsEl);

      return el;
    },
  }));

  // Use Tabulator in manual data mode (we handle pagination ourselves)
  tab._tabulator = new Tabulator(gridEl, {
    columns,
    data: [],
    layout: 'fitDataFill',
    height: '100%',
    placeholder: 'Loading data...',
    headerSortClickElement: 'icon',
    movableColumns: true,
    columnDefaults: {
      width: 150,
    },
  });

  // Sort handler — server-side
  tab._tabulator.on('headerClick', (e, column) => {
    const field = column.getField();
    if (tab._orderBy === field) {
      tab._dir = tab._dir === 'asc' ? 'desc' : 'asc';
    } else {
      tab._orderBy = field;
      tab._dir = 'asc';
    }
    tab._currentPage = 1;
    _fetchData(tab);
  });

  // Filter handler — server-side with debounce
  let filterTimer = null;
  tab._tabulator.on('dataFiltering', () => {
    // Tabulator fires this on header filter input — intercept for server-side
  });

  // Use MutationObserver on header filter inputs for debounced server-side filtering
  setTimeout(() => {
    const headerFilters = gridEl.querySelectorAll('.tabulator-header-filter input');
    headerFilters.forEach(input => {
      input.addEventListener('input', () => {
        clearTimeout(filterTimer);
        filterTimer = setTimeout(() => {
          _collectFilters(tab);
          tab._currentPage = 1;
          _fetchData(tab);
        }, 300);
      });
    });
  }, 200);
}

function _collectFilters(tab) {
  if (!tab._tabulator) return;
  const gridEl = document.getElementById('dwGrid');
  if (!gridEl) return;

  const parts = [];
  const headerCells = gridEl.querySelectorAll('.tabulator-col');
  headerCells.forEach(cell => {
    const field = cell.getAttribute('tabulator-field');
    const input = cell.querySelector('.tabulator-header-filter input');
    if (field && input && input.value.trim()) {
      parts.push(`${field}:contains:${input.value.trim()}`);
    }
  });
  tab._filters = parts.join(';');
}

// ---------------------------------------------------------------------------
// Server-side data fetch
// ---------------------------------------------------------------------------

async function _fetchData(tab) {
  if (!tab._tabulator) return;

  tab._tabulator.alert('Loading...');

  try {
    const params = {
      page: tab._currentPage,
      pageSize: tab._pageSize,
    };
    if (tab._orderBy) {
      params.orderBy = tab._orderBy;
      params.dir = tab._dir;
    }
    if (tab._filters) {
      params.where = tab._filters;
    }

    const result = await api.dbData(tab.tableName, params);
    tab._totalCount = result.totalCount || 0;
    tab._tabulator.setData(result.rows || []);
    tab._tabulator.clearAlert();

    _renderPagination(tab);
    _updateRowCount(tab);
  } catch (err) {
    tab._tabulator.clearAlert();
    tab._tabulator.alert(`Error: ${err.message}`, 'error');
  }
}

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

async function _fetchStats(tab) {
  try {
    const result = await api.dbStats(tab.tableName);
    tab._stats = result.stats || {};
    _renderStatsCards(tab);
  } catch {
    // Stats are supplementary — don't block the grid
  }
}

function _renderStatsCards(tab) {
  if (!tab._stats) return;

  for (const [colName, stats] of Object.entries(tab._stats)) {
    const el = document.getElementById(`dw-stats-${colName}`);
    if (!el) continue;

    const total = stats.total || 1;
    const nullPct = total > 0 ? Math.round((stats.nulls / total) * 100) : 0;
    const validPct = 100 - nullPct;

    // Distribution bar: blue = valid data, gray = nulls
    let barHTML = `<div class="dw-dist-bar" title="${validPct}% valid, ${nullPct}% null">`;
    if (validPct > 0) {
      barHTML += `<div class="dw-dist-fill" style="width:${validPct}%"></div>`;
    }
    if (nullPct > 0) {
      barHTML += `<div class="dw-dist-null" style="width:${nullPct}%"></div>`;
    }
    barHTML += '</div>';

    // Compact stats line below the bar
    const parts = [];
    parts.push(`${stats.distinct} distinct`);
    if (stats.min != null && stats.max != null) {
      parts.push(`${_truncStat(stats.min)} – ${_truncStat(stats.max)}`);
    }
    if (nullPct > 0) {
      parts.push(`${nullPct}% null`);
    }

    el.innerHTML = barHTML + `<div class="dw-stat-line">${parts.join(' · ')}</div>`;
  }
}

function _truncStat(val) {
  const s = String(val);
  return s.length > 12 ? s.slice(0, 12) + '\u2026' : s;
}

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

function _renderPagination(tab) {
  const viewer = refs.fileViewer;
  if (!viewer) return;

  // Remove existing pagination
  const existing = viewer.querySelector('.dw-pagination');
  if (existing) existing.remove();

  const totalPages = Math.max(1, Math.ceil(tab._totalCount / tab._pageSize));

  const pag = document.createElement('div');
  pag.className = 'dw-pagination';
  pag.innerHTML = `
    <button class="dw-page-btn" data-action="prev" ${tab._currentPage <= 1 ? 'disabled' : ''}>\u25C0</button>
    <span class="dw-page-info">Page ${tab._currentPage} of ${totalPages}</span>
    <button class="dw-page-btn" data-action="next" ${tab._currentPage >= totalPages ? 'disabled' : ''}>\u25B6</button>
  `;

  pag.querySelectorAll('.dw-page-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      if (btn.dataset.action === 'prev' && tab._currentPage > 1) {
        tab._currentPage--;
        _fetchData(tab);
      } else if (btn.dataset.action === 'next' && tab._currentPage < totalPages) {
        tab._currentPage++;
        _fetchData(tab);
      }
    });
  });

  const wrangler = viewer.querySelector('.data-wrangler');
  if (wrangler) wrangler.appendChild(pag);
}

function _updateRowCount(tab) {
  const el = document.getElementById('dwRowCount');
  if (el) el.textContent = `${tab._totalCount.toLocaleString()} rows`;
}
